import { useSyncExternalStore } from 'react';

import { grantSignedInAt, needsFreshSignIn, type GrantSource } from '@/lib/auth/scopes';

export const SIGN_IN_AGAIN_NOTICE = 'Sign in again to finish updating your library access';

/** Asks LibraryAccessWatch, inside the auth provider, to start the normal sign-in. */
export const SIGN_IN_AGAIN_EVENT = 'knowsh:sign-in-again';

// Held in memory only: a sign-in leaves the page and comes back to a fresh
// one, which is what clears a report from k. Nothing here ever redirects.
let staleGrant = false;
let reportedByAgent = false;
// When this grant's sign-in happened, from the grant itself (the identity host's clock); null until one is reported.
let signedInAt: number | null = null;
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((listener) => listener());

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const snapshot = () => staleGrant || reportedByAgent;

/** Called with the signed-in user after sign-in and after every renewal. */
export function reportGrant(user: GrantSource | null | undefined): void {
  signedInAt = user ? grantSignedInAt(user) : null;
  const stale = user ? needsFreshSignIn(user) : false;
  if (stale === staleGrant) return;
  staleGrant = stale;
  notify();
}

/**
 * k. ended a turn because its sign-in could not reach the library tools.
 * `endedAt` is when that turn ended: a failure from before this sign-in says
 * nothing about it, and is not reported.
 */
export function reportLibraryScopesMissing(endedAt?: string | number | null): void {
  const at = typeof endedAt === 'string' ? Date.parse(endedAt) : (endedAt ?? Number.NaN);
  if (signedInAt !== null && Number.isFinite(at) && at < signedInAt) return;
  if (reportedByAgent) return;
  reportedByAgent = true;
  notify();
}

/** A fresh page, as after a sign-in. */
export function resetLibraryAccess(): void {
  signedInAt = null;
  staleGrant = false;
  reportedByAgent = false;
  notify();
}

export function useNeedsSignInAgain(): boolean {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

export function signInAgain(): void {
  window.dispatchEvent(new CustomEvent(SIGN_IN_AGAIN_EVENT));
}
